import React, { useState, Fragment, memo } from 'react';
import { Sparkles, Zap, Clock, Target, TrendingUp, Settings, Play, Pause, Plus, Calendar, Users, Mail, Building2, Briefcase, ArrowRight, CheckCircle2, Edit, AlertCircle, X, MapPin } from 'lucide-react';
const emailSequences = [{
  id: 1,
  name: 'Welcome Series - Nuovi Brand',
  description: 'Serie di 3 email per brand appena registrati',
  status: 'active',
  trigger: 'Nuovo brand registrato',
  sent: 24,
  opened: 18,
  clicked: 12
}, {
  id: 2,
  name: 'Follow-up Trattative',
  description: 'Email automatica dopo 7 giorni senza risposta',
  status: 'active',
  trigger: 'Nessuna risposta dopo 7 giorni',
  sent: 15,
  opened: 12,
  clicked: 8
}, {
  id: 3,
  name: 'Reminder Appuntamenti',
  description: 'Promemoria 24h prima degli appuntamenti',
  status: 'paused',
  trigger: '24h prima appuntamento',
  sent: 8,
  opened: 7,
  clicked: 5
}];
interface MatchingRule {
  id: string;
  scenario: 'new_requirement' | 'new_property';
  enabled: boolean;
  name: string;
  description: string;
  conditions: {
    geographicExclusions?: {
      enabled: boolean;
      minimumDistanceKm?: number;
      excludeExistingLocations?: boolean; // Nuovo: esclude zone dove azienda ha già locali
    };
    competitiveAnalysis?: {
      enabled: boolean;
      preferLowCompetition?: boolean; // Preferisce aree poco competitive
      avoidSaturatedAreas?: boolean; // Evita aree sature
    };
    qualitativeCriteria?: {
      floorRequirements?: string[];
      windowsRequired?: boolean;
      parkingRequired?: boolean;
      accessibilityRequired?: boolean;
      outdoorSpaceRequired?: boolean;
      recentRenovation?: boolean;
      customizationPossible?: boolean;
    };
  };
  stats: {
    triggered: number;
    emailsSent: number;
    matches: number;
  };
}
const matchingRules: MatchingRule[] = [{
  id: '1',
  scenario: 'new_requirement',
  enabled: true,
  name: 'Match Nuove Esigenze → Immobili',
  description: 'Quando un brand esprime una nuova esigenza, AI cerca immobili compatibili evitando cannibalizzazione',
  conditions: {
    geographicExclusions: {
      enabled: true,
      minimumDistanceKm: 0.5,
      excludeExistingLocations: true
    },
    competitiveAnalysis: {
      enabled: true,
      preferLowCompetition: true,
      avoidSaturatedAreas: false
    },
    qualitativeCriteria: {
      floorRequirements: ['ground_floor'],
      windowsRequired: true,
      accessibilityRequired: true,
      customizationPossible: true
    }
  },
  stats: {
    triggered: 12,
    emailsSent: 45,
    matches: 38
  }
}, {
  id: '2',
  scenario: 'new_property',
  enabled: true,
  name: 'Match Nuovi Immobili → Aziende',
  description: 'Quando viene aggiunto un immobile, AI cerca brand interessati con criteri strategici',
  conditions: {
    geographicExclusions: {
      enabled: true,
      minimumDistanceKm: 1,
      excludeExistingLocations: false
    },
    competitiveAnalysis: {
      enabled: false,
      preferLowCompetition: false,
      avoidSaturatedAreas: false
    },
    qualitativeCriteria: {
      floorRequirements: ['ground_floor'],
      windowsRequired: true,
      recentRenovation: true
    }
  },
  stats: {
    triggered: 8,
    emailsSent: 32,
    matches: 28
  }
}];
export function Automation() {
  const [activeMainTab, setActiveMainTab] = useState<'sequences' | 'matching'>('sequences');
  const [sequenceTab, setSequenceTab] = useState<'active' | 'paused'>('active');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<MatchingRule | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<'new_requirement' | 'new_property' | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const filteredSequences = emailSequences.filter(seq => sequenceTab === 'active' ? seq.status === 'active' : seq.status === 'paused');
  const requirementRules = matchingRules.filter(r => r.scenario === 'new_requirement');
  const propertyRules = matchingRules.filter(r => r.scenario === 'new_property');
  return <div className="space-y-6" data-id="element-577">
      <div className="flex items-center justify-between" data-id="element-578">
        <div data-id="element-579">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-580">
            Automazione AI
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-581">
            Gestisci sequenze email e logiche di matching automatico
          </p>
        </div>
      </div>
      {/* Main Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-582">
        <div className="border-b border-gray-200" data-id="element-583">
          <div className="flex" data-id="element-584">
            <button onClick={() => setActiveMainTab('sequences')} className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${activeMainTab === 'sequences' ? 'border-[#1388ad] text-[#1388ad] bg-[#1388ad]/5' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-585">
              <Mail className="w-4 h-4" data-id="element-586" />
              Sequenze Email
              <span className={`px-2 py-0.5 text-xs rounded-full ${activeMainTab === 'sequences' ? 'bg-[#1388ad] text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-587">
                {emailSequences.length}
              </span>
            </button>
            <button onClick={() => setActiveMainTab('matching')} className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${activeMainTab === 'matching' ? 'border-[#1388ad] text-[#1388ad] bg-[#1388ad]/5' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-588">
              <Sparkles className="w-4 h-4" data-id="element-589" />
              Logiche Matching AI
              <span className={`px-2 py-0.5 text-xs rounded-full ${activeMainTab === 'matching' ? 'bg-[#1388ad] text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-590">
                {matchingRules.filter(r => r.enabled).length}
              </span>
            </button>
          </div>
        </div>
      </div>
      {/* Email Sequences Tab */}
      {activeMainTab === 'sequences' && <div className="space-y-6" data-id="element-591">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-592">
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-593">
              <div className="flex items-center gap-3 mb-2" data-id="element-594">
                <div className="w-10 h-10 bg-[#1388ad]/10 rounded-lg flex items-center justify-center" data-id="element-595">
                  <Zap className="w-5 h-5 text-[#1388ad]" data-id="element-596" />
                </div>
                <div data-id="element-597">
                  <p className="text-sm text-gray-500" data-id="element-598">Sequenze Attive</p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-599">
                    {emailSequences.filter(s => s.status === 'active').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-600">
              <div className="flex items-center gap-3 mb-2" data-id="element-601">
                <div className="w-10 h-10 bg-[#1388ad]/10 rounded-lg flex items-center justify-center" data-id="element-602">
                  <Mail className="w-5 h-5 text-[#1388ad]" data-id="element-603" />
                </div>
                <div data-id="element-604">
                  <p className="text-sm text-gray-500" data-id="element-605">Email Inviate</p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-606">
                    {emailSequences.reduce((sum, s) => sum + s.sent, 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-607">
              <div className="flex items-center gap-3 mb-2" data-id="element-608">
                <div className="w-10 h-10 bg-[#1388ad]/10 rounded-lg flex items-center justify-center" data-id="element-609">
                  <TrendingUp className="w-5 h-5 text-[#1388ad]" data-id="element-610" />
                </div>
                <div data-id="element-611">
                  <p className="text-sm text-gray-500" data-id="element-612">Tasso Apertura</p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-613">78%</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-614">
              <div className="flex items-center gap-3 mb-2" data-id="element-615">
                <div className="w-10 h-10 bg-[#1388ad]/10 rounded-lg flex items-center justify-center" data-id="element-616">
                  <Target className="w-5 h-5 text-[#1388ad]" data-id="element-617" />
                </div>
                <div data-id="element-618">
                  <p className="text-sm text-gray-500" data-id="element-619">Conversioni</p>
                  <p className="text-2xl font-semibold text-gray-900" data-id="element-620">25</p>
                </div>
              </div>
            </div>
          </div>
          {/* Sub Tabs */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-621">
            <div className="border-b border-gray-200" data-id="element-622">
              <div className="flex" data-id="element-623">
                <button onClick={() => setSequenceTab('active')} className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${sequenceTab === 'active' ? 'border-[#1388ad] text-[#1388ad] bg-[#1388ad]/5' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-624">
                  <Play className="w-4 h-4" data-id="element-625" />
                  Attive
                </button>
                <button onClick={() => setSequenceTab('paused')} className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${sequenceTab === 'paused' ? 'border-[#1388ad] text-[#1388ad] bg-[#1388ad]/5' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-626">
                  <Pause className="w-4 h-4" data-id="element-627" />
                  In Pausa
                </button>
              </div>
            </div>
          </div>
          {/* Sequences List */}
          <div className="space-y-4" data-id="element-628">
            <div className="flex justify-end" data-id="element-629">
              <button className="flex items-center gap-2 px-4 py-2 bg-[#1388ad] text-white rounded-lg hover:bg-[#0f6b8a] transition-colors" data-id="element-630">
                <Plus className="w-4 h-4" data-id="element-631" />
                Nuova Sequenza
              </button>
            </div>
            {filteredSequences.map(sequence => {
          const openRate = (sequence.opened / sequence.sent * 100).toFixed(1);
          const clickRate = (sequence.clicked / sequence.sent * 100).toFixed(1);
          return <div key={sequence.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow" data-id="element-632">
                  <div className="flex items-start justify-between mb-4" data-id="element-633">
                    <div className="flex items-start gap-4" data-id="element-634">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#1388ad]/20 to-[#0f6b8a]/20 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-635">
                        <Mail className="w-6 h-6 text-[#1388ad]" data-id="element-636" />
                      </div>
                      <div data-id="element-637">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-638">
                          {sequence.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-639">
                          {sequence.description}
                        </p>
                        <div className="flex items-center gap-2" data-id="element-640">
                          <Clock className="w-4 h-4 text-gray-400" data-id="element-641" />
                          <span className="text-sm text-gray-600" data-id="element-642">
                            Trigger: {sequence.trigger}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-643">
                      {sequence.status === 'active' ? <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-644">
                          <Pause className="w-4 h-4 text-gray-600" data-id="element-645" />
                        </button> : <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-646">
                          <Play className="w-4 h-4 text-gray-600" data-id="element-647" />
                        </button>}
                      <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-648">
                        <Settings className="w-4 h-4 text-gray-600" data-id="element-649" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200" data-id="element-650">
                    <div data-id="element-651">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-652">
                        Email Inviate
                      </p>
                      <p className="text-lg font-semibold text-gray-900" data-id="element-653">
                        {sequence.sent}
                      </p>
                    </div>
                    <div data-id="element-654">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-655">
                        Tasso Apertura
                      </p>
                      <p className="text-lg font-semibold text-green-600" data-id="element-656">
                        {openRate}%
                      </p>
                    </div>
                    <div data-id="element-657">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-658">Tasso Click</p>
                      <p className="text-lg font-semibold text-blue-600" data-id="element-659">
                        {clickRate}%
                      </p>
                    </div>
                  </div>
                </div>;
        })}
          </div>
        </div>}
      {/* AI Matching Logic Tab */}
      {activeMainTab === 'matching' && <div className="space-y-6" data-id="element-660">
          {/* Info Banner */}
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-661">
            <div className="flex items-start gap-4" data-id="element-662">
              <div className="flex-1" data-id="element-663">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-664">
                  EDA Intelligence - Matching Automatico
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-665">
                  L'AI analizza automaticamente nuove esigenze e immobili per
                  creare match perfetti e inviare email personalizzate con le
                  schede più rilevanti.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-666">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-667">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-668">
                      Match Totali Creati
                    </p>
                    <p className="text-xl font-semibold text-[#1388ad]" data-id="element-669">
                      {matchingRules.reduce((sum, r) => sum + r.stats.matches, 0)}
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-670">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-671">Email Inviate</p>
                    <p className="text-xl font-semibold text-[#1388ad]" data-id="element-672">
                      {matchingRules.reduce((sum, r) => sum + r.stats.emailsSent, 0)}
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-673">
                    <p className="text-xs text-gray-600 mb-1" data-id="element-674">Tasso Successo</p>
                    <p className="text-xl font-semibold text-green-600" data-id="element-675">87%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Scenario 1: New Requirement */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-676">
            <div className="bg-white px-6 py-4 border-b border-gray-200" data-id="element-677">
              <div className="flex items-center justify-between" data-id="element-678">
                <div className="flex items-center gap-3" data-id="element-679">
                  <div className="w-10 h-10 bg-[#1388ad] rounded-lg flex items-center justify-center" data-id="element-680">
                    <Briefcase className="w-5 h-5 text-white" data-id="element-681" />
                  </div>
                  <div data-id="element-682">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-683">
                      Nuova Esigenza → Match Immobili
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-684">
                      Quando un brand esprime una nuova esigenza
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setSelectedScenario('new_requirement');
              setSelectedRule(null);
              setShowRuleModal(true);
            }} className="flex items-center gap-2 px-4 py-2 bg-[#1388ad] text-white rounded-lg hover:bg-[#0f6b8a] transition-colors" data-id="element-685">
                  <Plus className="w-4 h-4" data-id="element-686" />
                  Nuova Regola
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4" data-id="element-687">
              {requirementRules.length === 0 ? <div className="text-center py-8" data-id="element-688">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-689" />
                  <p className="text-sm text-gray-500" data-id="element-690">
                    Nessuna regola configurata per questo scenario
                  </p>
                </div> : requirementRules.map(rule => <div key={rule.id} className="border border-gray-200 rounded-lg p-4" data-id="element-691">
                    <div className="flex items-start justify-between mb-3" data-id="element-692">
                      <div className="flex-1" data-id="element-693">
                        <div className="flex items-center gap-3 mb-2" data-id="element-694">
                          <h4 className="text-base font-semibold text-gray-900" data-id="element-695">
                            {rule.name}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-696">
                            {rule.enabled ? 'Attiva' : 'Disattivata'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3" data-id="element-697">
                          {rule.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3" data-id="element-698">
                          {rule.conditions.geographicExclusions?.enabled && <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-699">
                              Distanza minima:{' '}
                              {rule.conditions.geographicExclusions.minimumDistanceKm}
                              km
                            </span>}
                          {rule.conditions.competitiveAnalysis?.enabled && <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-700">
                              Analisi competitività:{' '}
                              {rule.conditions.competitiveAnalysis.preferLowCompetition ? 'Preferisce aree poco competitive' : 'Evita aree sature'}
                            </span>}
                          {rule.conditions.qualitativeCriteria?.floorRequirements && <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-701">
                              Piano:{' '}
                              {rule.conditions.qualitativeCriteria.floorRequirements.join(', ')}
                            </span>}
                        </div>
                        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200" data-id="element-702">
                          <div data-id="element-703">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-704">
                              Volte Attivata
                            </p>
                            <p className="text-lg font-semibold text-gray-900" data-id="element-705">
                              {rule.stats.triggered}
                            </p>
                          </div>
                          <div data-id="element-706">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-707">
                              Email Inviate
                            </p>
                            <p className="text-lg font-semibold text-[#1388ad]" data-id="element-708">
                              {rule.stats.emailsSent}
                            </p>
                          </div>
                          <div data-id="element-709">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-710">
                              Match Creati
                            </p>
                            <p className="text-lg font-semibold text-green-600" data-id="element-711">
                              {rule.stats.matches}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4" data-id="element-712">
                        <button onClick={() => {
                  setSelectedRule(rule);
                  setSelectedScenario('new_requirement');
                  setShowRuleModal(true);
                }} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-713">
                          <Edit className="w-4 h-4" data-id="element-714" />
                        </button>
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>
          {/* Scenario 2: New Property */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-715">
            <div className="bg-white px-6 py-4 border-b border-gray-200" data-id="element-716">
              <div className="flex items-center justify-between" data-id="element-717">
                <div className="flex items-center gap-3" data-id="element-718">
                  <div className="w-10 h-10 bg-[#1388ad] rounded-lg flex items-center justify-center" data-id="element-719">
                    <Building2 className="w-5 h-5 text-white" data-id="element-720" />
                  </div>
                  <div data-id="element-721">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-722">
                      Nuovo Immobile → Match Aziende
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-723">
                      Quando viene aggiunto un nuovo immobile
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setSelectedScenario('new_property');
              setSelectedRule(null);
              setShowRuleModal(true);
            }} className="flex items-center gap-2 px-4 py-2 bg-[#1388ad] text-white rounded-lg hover:bg-[#0f6b8a] transition-colors" data-id="element-724">
                  <Plus className="w-4 h-4" data-id="element-725" />
                  Nuova Regola
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4" data-id="element-726">
              {propertyRules.length === 0 ? <div className="text-center py-8" data-id="element-727">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-728" />
                  <p className="text-sm text-gray-500" data-id="element-729">
                    Nessuna regola configurata per questo scenario
                  </p>
                </div> : propertyRules.map(rule => <div key={rule.id} className="border border-gray-200 rounded-lg p-4" data-id="element-730">
                    <div className="flex items-start justify-between mb-3" data-id="element-731">
                      <div className="flex-1" data-id="element-732">
                        <div className="flex items-center gap-3 mb-2" data-id="element-733">
                          <h4 className="text-base font-semibold text-gray-900" data-id="element-734">
                            {rule.name}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-735">
                            {rule.enabled ? 'Attiva' : 'Disattivata'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3" data-id="element-736">
                          {rule.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-3" data-id="element-737">
                          {rule.conditions.geographicExclusions?.enabled && <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-738">
                              Distanza minima:{' '}
                              {rule.conditions.geographicExclusions.minimumDistanceKm}
                              km
                            </span>}
                          {rule.conditions.competitiveAnalysis?.enabled && <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-739">
                              Analisi competitività:{' '}
                              {rule.conditions.competitiveAnalysis.preferLowCompetition ? 'Preferisce aree poco competitive' : 'Evita aree sature'}
                            </span>}
                          {rule.conditions.qualitativeCriteria?.floorRequirements && <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full" data-id="element-740">
                              Piano:{' '}
                              {rule.conditions.qualitativeCriteria.floorRequirements.join(', ')}
                            </span>}
                        </div>
                        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200" data-id="element-741">
                          <div data-id="element-742">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-743">
                              Volte Attivata
                            </p>
                            <p className="text-lg font-semibold text-gray-900" data-id="element-744">
                              {rule.stats.triggered}
                            </p>
                          </div>
                          <div data-id="element-745">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-746">
                              Email Inviate
                            </p>
                            <p className="text-lg font-semibold text-[#1388ad]" data-id="element-747">
                              {rule.stats.emailsSent}
                            </p>
                          </div>
                          <div data-id="element-748">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-749">
                              Match Creati
                            </p>
                            <p className="text-lg font-semibold text-green-600" data-id="element-750">
                              {rule.stats.matches}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4" data-id="element-751">
                        <button onClick={() => {
                  setSelectedRule(rule);
                  setSelectedScenario('new_property');
                  setShowRuleModal(true);
                }} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-752">
                          <Edit className="w-4 h-4" data-id="element-753" />
                        </button>
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>
        </div>}
      {/* Rule Configuration Modal */}
      {showRuleModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-id="element-754">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-755">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10" data-id="element-756">
              <div className="flex items-center justify-between mb-4" data-id="element-757">
                <div data-id="element-758">
                  <h2 className="text-xl font-semibold text-gray-900" data-id="element-759">
                    {selectedRule ? 'Modifica Regola di Matching' : 'Nuova Regola di Matching'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-760">
                    {selectedScenario === 'new_requirement' ? "L'AI cercherà automaticamente immobili compatibili basandosi sui dati dell'esigenza" : "L'AI cercherà automaticamente brand interessati basandosi sui dati dell'immobile"}
                  </p>
                </div>
                <button onClick={() => {
              setShowRuleModal(false);
              setCurrentStep(1);
            }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-761">
                  <X className="w-5 h-5" data-id="element-762" />
                </button>
              </div>
              {/* Progress Steps */}
              <div className="flex items-center justify-between" data-id="element-763">
                {[{
              num: 1,
              label: 'Info Base'
            }, {
              num: 2,
              label: 'Criteri Strategici'
            }, {
              num: 3,
              label: 'Riepilogo'
            }].map((step, index) => <Fragment key={step.num} data-id="element-764">
                    <div className="flex flex-col items-center" data-id="element-765">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${currentStep === step.num ? 'bg-[#1388ad] text-white' : currentStep > step.num ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-766">
                        {currentStep > step.num ? <CheckCircle2 className="w-5 h-5" data-id="element-767" /> : step.num}
                      </div>
                      <span className="text-xs text-gray-600 mt-1 text-center" data-id="element-768">
                        {step.label}
                      </span>
                    </div>
                    {index < 2 && <div className={`flex-1 h-0.5 mx-2 transition-colors ${currentStep > step.num ? 'bg-green-500' : 'bg-gray-200'}`} data-id="element-769" />}
                  </Fragment>)}
              </div>
            </div>
            <div className="p-6" data-id="element-770">
              {/* Step 1: Basic Info */}
              {currentStep === 1 && <div className="space-y-6" data-id="element-771">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-772">
                    <div className="flex items-start gap-3" data-id="element-773">
                      <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-774" />
                      <div data-id="element-775">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-776">
                          Informazioni di base
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-777">
                          {selectedScenario === 'new_requirement' ? "L'AI analizzerà automaticamente le esigenze (budget, metratura, zone, categorie) e troverà immobili compatibili. Tu configuri solo i criteri strategici." : "L'AI analizzerà automaticamente gli immobili (caratteristiche, zona, prezzo) e troverà brand interessati. Tu configuri solo i criteri strategici."}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-778">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-779">
                      Nome della Regola *
                    </label>
                    <input type="text" defaultValue={selectedRule?.name} placeholder={selectedScenario === 'new_requirement' ? 'Es: Matching Esigenze - No Cannibalizzazione' : 'Es: Matching Immobili - Zone Strategiche'} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-780" />
                  </div>
                  <div data-id="element-781">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-782">
                      Descrizione
                    </label>
                    <textarea rows={3} defaultValue={selectedRule?.description} placeholder={selectedScenario === 'new_requirement' ? 'Es: Match immobili evitando zone dove il brand ha già locali, con distanza minima di 500m' : 'Es: Match brand interessati considerando aree poco competitive e caratteristiche premium'} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-783" />
                  </div>
                </div>}
              {/* Step 2: Strategic Criteria */}
              {currentStep === 2 && <div className="space-y-6" data-id="element-784">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-785">
                    <div className="flex items-start gap-3" data-id="element-786">
                      <Target className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-787" />
                      <div data-id="element-788">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-789">
                          Criteri strategici di matching
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-790">
                          Configura le regole qualitative che l'AI userà per
                          filtrare e prioritizzare i match.
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Geographic Exclusions */}
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-791">
                    <div className="flex items-start gap-3 mb-4" data-id="element-792">
                      <input type="checkbox" defaultChecked={selectedRule?.conditions.geographicExclusions?.enabled} className="rounded w-5 h-5 text-[#1388ad] focus:ring-[#1388ad] mt-0.5" data-id="element-793" />
                      <div className="flex-1" data-id="element-794">
                        <label className="text-sm font-semibold text-gray-900 block mb-1" data-id="element-795">
                          Criteri Geografici
                        </label>
                        <p className="text-xs text-gray-600" data-id="element-796">
                          {selectedScenario === 'new_requirement' ? 'Gestisci come evitare cannibalizzazione tra punti vendita' : 'Gestisci preferenze geografiche per i brand'}
                        </p>
                      </div>
                    </div>
                    <div className="ml-8 space-y-4" data-id="element-797">
                      <div className="flex items-start gap-3" data-id="element-798">
                        <input type="checkbox" defaultChecked={selectedRule?.conditions.geographicExclusions?.excludeExistingLocations} className="rounded w-4 h-4 text-[#1388ad] focus:ring-[#1388ad] mt-0.5" data-id="element-799" />
                        <div className="flex-1" data-id="element-800">
                          <label className="text-sm font-medium text-gray-900 block mb-1" data-id="element-801">
                            {selectedScenario === 'new_requirement' ? 'Escludi zone con locali esistenti' : 'Escludi brand con locali nella zona'}
                          </label>
                          <p className="text-xs text-gray-500" data-id="element-802">
                            {selectedScenario === 'new_requirement' ? "Non proporre immobili in zone dove l'azienda ha già punti vendita" : "Non proporre l'immobile a brand che hanno già locali nella zona"}
                          </p>
                        </div>
                      </div>
                      <div data-id="element-803">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-804">
                          Distanza Minima tra Locali (km)
                        </label>
                        <input type="number" step="0.1" min="0" defaultValue={selectedRule?.conditions.geographicExclusions?.minimumDistanceKm || 0.5} placeholder="0.5" className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1388ad]" data-id="element-805" />
                        <p className="text-xs text-gray-500 mt-2" data-id="element-806">
                          {selectedScenario === 'new_requirement' ? "Distanza minima richiesta tra il nuovo immobile e altri locali dell'azienda" : 'Distanza minima richiesta tra questo immobile e altri locali del brand'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Competitive Analysis */}
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-807">
                    <div className="flex items-start gap-3 mb-4" data-id="element-808">
                      <input type="checkbox" defaultChecked={selectedRule?.conditions.competitiveAnalysis?.enabled} className="rounded w-5 h-5 text-[#1388ad] focus:ring-[#1388ad] mt-0.5" data-id="element-809" />
                      <div className="flex-1" data-id="element-810">
                        <label className="text-sm font-semibold text-gray-900 block mb-1" data-id="element-811">
                          Analisi Competitività
                        </label>
                        <p className="text-xs text-gray-600" data-id="element-812">
                          Considera il livello di competizione nell'area per
                          ottimizzare i match
                        </p>
                      </div>
                    </div>
                    <div className="ml-8 space-y-4" data-id="element-813">
                      <div className="flex items-start gap-3" data-id="element-814">
                        <input type="checkbox" defaultChecked={selectedRule?.conditions.competitiveAnalysis?.preferLowCompetition} className="rounded w-4 h-4 text-[#1388ad] focus:ring-[#1388ad] mt-0.5" data-id="element-815" />
                        <div className="flex-1" data-id="element-816">
                          <label className="text-sm font-medium text-gray-900 block mb-1" data-id="element-817">
                            Preferisci aree poco competitive
                          </label>
                          <p className="text-xs text-gray-500" data-id="element-818">
                            Prioritizza zone con minore densità di competitor
                            diretti
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-819">
                        <input type="checkbox" defaultChecked={selectedRule?.conditions.competitiveAnalysis?.avoidSaturatedAreas} className="rounded w-4 h-4 text-[#1388ad] focus:ring-[#1388ad] mt-0.5" data-id="element-820" />
                        <div className="flex-1" data-id="element-821">
                          <label className="text-sm font-medium text-gray-900 block mb-1" data-id="element-822">
                            Evita aree sature
                          </label>
                          <p className="text-xs text-gray-500" data-id="element-823">
                            Escludi zone con eccessiva concentrazione di
                            attività simili
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Qualitative Criteria */}
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-824">
                    <div className="mb-4" data-id="element-825">
                      <label className="text-sm font-semibold text-gray-900 block mb-1" data-id="element-826">
                        Criteri Qualitativi Immobile
                      </label>
                      <p className="text-xs text-gray-600" data-id="element-827">
                        Caratteristiche fisiche richieste per l'immobile
                      </p>
                    </div>
                    <div className="space-y-3" data-id="element-828">
                      <div data-id="element-829">
                        <label className="text-xs font-medium text-gray-700 block mb-2" data-id="element-830">
                          Piano Preferito
                        </label>
                        <div className="space-y-2" data-id="element-831">
                          {[{
                      value: 'ground_floor',
                      label: 'Piano Terra'
                    }, {
                      value: 'first_floor',
                      label: 'Primo Piano'
                    }, {
                      value: 'any',
                      label: 'Qualsiasi Piano'
                    }].map(option => <label key={option.value} className="flex items-center gap-2 text-sm" data-id="element-832">
                              <input type="checkbox" defaultChecked={selectedRule?.conditions.qualitativeCriteria?.floorRequirements?.includes(option.value)} className="rounded w-4 h-4 text-[#1388ad] focus:ring-[#1388ad]" data-id="element-833" />
                              {option.label}
                            </label>)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200" data-id="element-834">
                        {[{
                    key: 'windowsRequired',
                    label: 'Vetrine su Strada'
                  }, {
                    key: 'parkingRequired',
                    label: 'Parcheggio'
                  }, {
                    key: 'accessibilityRequired',
                    label: 'Accessibilità'
                  }, {
                    key: 'outdoorSpaceRequired',
                    label: 'Spazio Esterno'
                  }, {
                    key: 'recentRenovation',
                    label: 'Recente Ristrutturazione'
                  }, {
                    key: 'customizationPossible',
                    label: 'Personalizzazione'
                  }].map(criterion => <label key={criterion.key} className="flex items-center gap-2 text-sm" data-id="element-835">
                            <input type="checkbox" defaultChecked={selectedRule?.conditions.qualitativeCriteria?.[criterion.key as keyof typeof selectedRule.conditions.qualitativeCriteria]} className="rounded w-4 h-4 text-[#1388ad] focus:ring-[#1388ad]" data-id="element-836" />
                            {criterion.label}
                          </label>)}
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Step 3: Summary */}
              {currentStep === 3 && <div className="space-y-6" data-id="element-837">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-838">
                    <div className="flex items-start gap-3" data-id="element-839">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-840" />
                      <div data-id="element-841">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-842">
                          Riepilogo della regola
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-843">
                          Controlla i criteri strategici configurati. La regola
                          si attiverà automaticamente quando le condizioni
                          saranno soddisfatte.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-844">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-845">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-846">
                        Come funzionerà questa regola
                      </h4>
                      <div className="space-y-3 text-sm text-gray-700" data-id="element-847">
                        <div className="flex items-start gap-2" data-id="element-848">
                          <span className="text-[#1388ad] font-bold" data-id="element-849">1.</span>
                          <p data-id="element-850">
                            {selectedScenario === 'new_requirement' ? "Quando un brand esprime una nuova esigenza, l'AI analizza automaticamente budget, metratura, zone preferite e categorie" : "Quando viene aggiunto un nuovo immobile, l'AI analizza automaticamente caratteristiche, zona, prezzo e tipologia"}
                          </p>
                        </div>
                        <div className="flex items-start gap-2" data-id="element-851">
                          <span className="text-[#1388ad] font-bold" data-id="element-852">2.</span>
                          <p data-id="element-853">
                            L'AI applica i criteri strategici configurati:
                            esclusioni geografiche, analisi competitività e
                            requisiti qualitativi
                          </p>
                        </div>
                        <div className="flex items-start gap-2" data-id="element-854">
                          <span className="text-[#1388ad] font-bold" data-id="element-855">3.</span>
                          <p data-id="element-856">
                            Verifica distanze minime e zone da evitare per
                            prevenire cannibalizzazione
                          </p>
                        </div>
                        <div className="flex items-start gap-2" data-id="element-857">
                          <span className="text-[#1388ad] font-bold" data-id="element-858">4.</span>
                          <p data-id="element-859">
                            Per ogni match trovato, genera una email
                            personalizzata con analisi dettagliata e score di
                            compatibilità
                          </p>
                        </div>
                        <div className="flex items-start gap-2" data-id="element-860">
                          <span className="text-[#1388ad] font-bold" data-id="element-861">5.</span>
                          <p data-id="element-862">
                            Invia automaticamente le email ai destinatari
                            interessati
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-863">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-864">
                        Criteri Strategici Configurati
                      </h4>
                      <div className="space-y-3" data-id="element-865">
                        <div className="text-xs" data-id="element-866">
                          <span className="font-medium text-gray-700" data-id="element-867">
                            Scenario:
                          </span>
                          <p className="text-gray-600 mt-1" data-id="element-868">
                            {selectedScenario === 'new_requirement' ? 'Nuova Esigenza → Match Immobili Automatico' : 'Nuovo Immobile → Match Aziende Automatico'}
                          </p>
                        </div>
                        <div className="text-xs" data-id="element-869">
                          <span className="font-medium text-gray-700" data-id="element-870">
                            Cosa analizza l'AI automaticamente:
                          </span>
                          <ul className="text-gray-600 mt-1 space-y-1 ml-4" data-id="element-871">
                            {selectedScenario === 'new_requirement' ? <>
                                <li data-id="element-872">
                                  • Budget e range di prezzo dell'esigenza
                                </li>
                                <li data-id="element-873">• Metratura richiesta (min/max)</li>
                                <li data-id="element-874">• Zone geografiche preferite</li>
                                <li data-id="element-875">• Categoria brand e tipologie immobile</li>
                              </> : <>
                                <li data-id="element-876">• Prezzo e caratteristiche immobile</li>
                                <li data-id="element-877">• Zona e posizione geografica</li>
                                <li data-id="element-878">• Metratura e layout disponibile</li>
                                <li data-id="element-879">• Tipologia e categoria immobile</li>
                              </>}
                          </ul>
                        </div>
                        <div className="text-xs" data-id="element-880">
                          <span className="font-medium text-gray-700" data-id="element-881">
                            Criteri Strategici Applicati:
                          </span>
                          <ul className="text-gray-600 mt-1 space-y-1 ml-4" data-id="element-882">
                            <li data-id="element-883">
                              • Distanza minima: 500m (evita cannibalizzazione)
                            </li>
                            <li data-id="element-884">• Esclusione zone con locali esistenti</li>
                            <li data-id="element-885">• Preferenza per aree poco competitive</li>
                            <li data-id="element-886">
                              • Requisiti qualitativi: Piano terra, vetrine
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-6" data-id="element-887">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg" data-id="element-888">
                      <input type="checkbox" defaultChecked={selectedRule?.enabled ?? true} className="rounded w-5 h-5 text-[#1388ad] focus:ring-[#1388ad]" data-id="element-889" />
                      <div data-id="element-890">
                        <label className="text-sm font-medium text-gray-900 block" data-id="element-891">
                          Attiva questa regola immediatamente
                        </label>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-892">
                          La regola inizierà a funzionare subito dopo il
                          salvataggio
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Navigation Buttons */}
              <div className="flex gap-3 pt-6 border-t border-gray-200 mt-6" data-id="element-893">
                {currentStep > 1 && <button onClick={() => setCurrentStep(currentStep - 1)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-894">
                    Indietro
                  </button>}
                {currentStep < 3 ? <button onClick={() => setCurrentStep(currentStep + 1)} className="flex-1 px-6 py-2 bg-[#1388ad] text-white rounded-lg hover:bg-[#0f6b8a] transition-colors" data-id="element-895">
                    Avanti
                  </button> : <button className="flex-1 px-6 py-2 bg-[#1388ad] text-white rounded-lg hover:bg-[#0f6b8a] transition-colors" data-id="element-896">
                    {selectedRule ? 'Salva Modifiche' : 'Crea Regola'}
                  </button>}
                <button onClick={() => {
              setShowRuleModal(false);
              setCurrentStep(1);
            }} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-897">
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}